// EDEN · adapter from the designer's blocks (Module P2) onto src/tos.ts's TosAssessment — reused
// verbatim (already unit-tested against the department's real spreadsheet), never forked.
//
// tos.ts's own model: "marks are derived from time, never typed in." The designer's blocks do the
// opposite — the teacher types marks straight onto each block. The identity trick below reconciles
// the two without touching tos.ts: set each item's timeMinutes = its typed marks, and leave the
// paper's durationMin unset (null) so computeMarks falls back to the summed item time as its own
// basis. Then mark = paperMarks * timeMinutes / paperMinutes = totalMarks * marks / totalMarks =
// marks. IEEE-754 doesn't guarantee that division-then-multiplication is bit-exact for every input
// (verified: whole/half/quarter marks — the only values the editor's step="0.5" can produce — land
// exactly; an arbitrary decimal can be off by ~1e-15), but the error is always many orders of
// magnitude inside validate()'s own 1e-9 tolerance, so no rule can misfire either way. So every
// tos.ts rule that reads a computed mark sees, for every practical purpose, the number the teacher
// typed, and rules 2 ("time-mismatch") and 5 ("duration") — which only fire
// when durationMin is non-null — never fire on a value that was never really a duration.
//
// questionCode/itemNo are auto-numbered (the designer has no such field — nothing for the teacher
// to fill in or forget) and questionForm is a generic, non-listening value for every block (v1's
// listening blocks don't attach real audio — "the teacher plays it" — so tos.ts's LISTENING_FORMS
// media-required rule, aimed at the item bank's own controlled vocabulary, is not this designer's
// concern). isOpenResponse stays false throughout: every block type here structurally carries its
// own answer key, so tos.ts's "needs criteria, not Personal" rule would only ever be noise.
//
// ── WHAT JOB-W04 CHANGED HERE, 2026-09-18 ──────────────────────────────────────────────────────
//
// Two defects, both measured, both in the direction of a comfortable silence:
//
// `F-2026-09-18-05` — this file used to read a block's `bloom: null` as `"k"`. Every Item Bank
// import arrives untagged, so a paper built entirely from accepted cards reported **100%
// Knowledge** — not as a warning about missing data, but as a confident fact. The paper's real
// state is "no thinking level recorded", and that is now what it says: an untagged block is
// EXCLUDED from the split and counted in `bloomUntaggedMarks`, and `bloom-untagged` is an ERROR.
// It blocks the `tos_ok` gate; it does not block a save (nothing here ever blocked a save).
// Chosen deliberately: "level of thinking K/C/A" is a REQUIRED TOS column
// (`Assessments/assessment-standard.json` → tos.required_columns), so a paper without one is not a
// Table of Specifications at all. ⚠ Consequence, stated rather than slipped in: a paper that was
// previously `tos_ok` on untagged blocks now reads `draft` until someone tags them. It was never
// really TOS-OK; it only looked it.
//
// The difficulty half used to be a generic 20/60/20 SHARE with no rounding rule, no per-component
// dimension and no severity — it sat beside `violations`, never inside them, and said in its own
// comment that it never blocks anything. The department's actual rails now exist, read from
// `src/assessment-standard.ts` (GENERATED from `Assessments/assessment-standard.json`, never
// transcribed), with severities taken from the standard's own wording:
//
//   • `difficulty-count-*`  ERROR   — easy = difficult = round_half_up(0.20·n); medium = n−easy−difficult.
//                                     The standard: "the totals 6/18/6 may not [vary]" (§5.2).
//   • `difficulty-untagged` ERROR   — a component whose E+M+D does not equal its own item count.
//                                     EDEN_TOS_AppSpec.md:205,211 lists per-component difficulty as
//                                     BLOCKING: "grand totals alone let two compensating typos through".
//   • `difficulty-component-*` WARNING — a component's E/M/D against the frame's allocation. A
//                                     WARNING on purpose: the standard explicitly permits it —
//                                     "a setter may vary the per-component allocation" (§5.2).
//   • `difficulty-visual-reading` ERROR — Visual Reading carries at least one difficult item
//                                     (§10, :657-658). The COMPONENT is named from that prose —
//                                     the "always" belongs to it alone — and the COUNT is
//                                     corroborated against the frame by this file's own tests, so
//                                     the two sources cannot drift apart unnoticed. A first draft
//                                     derived the component from the frame instead and turned a
//                                     PERMITTED per-component variation into an error; the tests
//                                     caught it before it left the session.
//
// ⚠ These rails report REAL, unfixable-in-code failures on today's corpus, and that is the correct
// outcome: an Item Bank card carries ONE difficulty letter for all of its items, so a 3-item
// Spelling card cannot express the frame's 2 easy + 1 medium. That is owner question P4-4
// (`EDEN_BACKLOG.md:150`), and the rail is built to tell the truth about a corpus that cannot yet
// satisfy it — never weakened to make today's cards pass.

import {
  validate,
  bloomSplit,
  type Bloom,
  type TosAssessment,
  type TosItem,
  type Violation,
} from "./tos";
import {
  DIFFICULTY_RULE_TEXT,
  componentFrame,
  difficultyCountsFor,
  groupForGrade,
  type StandardBand,
} from "./assessment-standard";
import { itemBankProvenance, itemCountNotes } from "./itembank";
import { sectionLevel } from "./section-layout";
import type { EditorBlock } from "./blocks";

/** What the paper claims to be. A worksheet is classroom practice, not a Table of Specifications:
 *  the standard's component frame and the "every row names a component" rail are monthly/test
 *  rules (`EDEN_TOS_AppSpec.md`:202,212) and holding a two-block worksheet to them would be noise. */
export type PaperKind = "test" | "worksheet";

export const MONTHLY_ITEM_TARGET = 30;
export const MONTHLY_MARK_TARGET = 30;

/** Blocks that count toward marks/Bloom/difficulty — everything except pure instructions and
 *  section headings (a section's marks are the SUM of these, per section-layout.ts — counting the
 *  heading too would double them). */
export function countableBlocks(blocks: EditorBlock[]): EditorBlock[] {
  return blocks.filter((b) => b.type !== "instructions" && b.type !== "section");
}

/** The component a block belongs to, for the per-component rails. Item Bank blocks state it in
 *  their provenance; a hand-typed block states nothing, and is grouped under a name the frame
 *  deliberately does not contain, so it is reported as unallocated rather than silently excused. */
export const UNALLOCATED = "(not from a card)";

/**
 * Which component each countable block sits under.
 *
 * Two sources, in order: the card's own authored `component` (Item Bank provenance), then the
 * title of the enclosing level-1 `section` block — which is exactly what a section IS on a real
 * paper, and whose own placeholder in the editor is "Vocabulary". A hand-typed block under a
 * section titled "Grammar" is a Grammar row; that is how a teacher names a component without an
 * Item Bank card, and without it the per-component rails could only ever see imported blocks.
 *
 * ⚠ This began as a one-line function reading provenance alone. The independent review showed what
 * that cost: a 30-item hand-typed paper at 6/18/6 and 15k/9c/6a passed with ZERO violations,
 * because every row was "unallocated" and every per-component rail skipped it. `EDEN_TOS_AppSpec.md`
 * :212 lists "a row carrying marks or items under **no component name**" as BLOCKING.
 */
function componentsByBlock(blocks: EditorBlock[]): Map<string, string> {
  const out = new Map<string, string>();
  let current = "";
  for (const block of blocks) {
    if (block.type === "section") {
      // Only a level-1 section names a component; a sub-section divides one (section-layout.ts).
      if (sectionLevel(block.content) === 1) current = String(block.content.title ?? "").trim();
      continue;
    }
    if (block.type === "instructions") continue;
    const p = itemBankProvenance(block.content);
    const authored = p?.component?.trim();
    out.set(block.id, authored || current || UNALLOCATED);
  }
  return out;
}

function toTosAssessment(blocks: EditorBlock[]): TosAssessment | null {
  const items = countableBlocks(blocks);
  if (items.length === 0) return null;
  const totalMarks = items.reduce((sum, b) => sum + (b.marks || 0), 0);
  if (totalMarks <= 0) return null; // computeMarks requires a positive basis — nothing to show yet

  const tosItems: TosItem[] = items.map((b, i) => ({
    id: b.id,
    itemNo: i + 1,
    questionCode: String(i + 1),
    timeMinutes: b.marks,
    // ⚠ Untagged blocks still enter tos.ts as "k" so that every OTHER rule (section marks, item
    // numbering, question codes, media) keeps seeing the whole paper. tos.ts's own Bloom type has
    // no fourth value. The split this file REPORTS is computed separately, over tagged blocks
    // only, and tos.ts's bloom-split findings are dropped whenever anything is untagged — see
    // `summarize`. Nothing downstream ever sees this placeholder as a fact about the paper.
    bloom: (b.bloom ?? "k") as Bloom,
    questionForm: "designer_block",
    isOpenResponse: false,
    media: null,
    stat: null,
  }));

  return {
    id: "designer-draft",
    kind: "monthly",
    paper: 1, // never 2 — see file header: durationMin stays null, so paper's own duration rule (2) is moot either way
    grade: 0,
    totalMarks,
    durationMin: null,
    sections: [{ id: "s1", roman: "I", title: "Paper", printedMarks: totalMarks, items: tosItems }],
  };
}

export interface DifficultyCount {
  band: StandardBand;
  label: string;
  /** Items actually tagged into this band. */
  items: number;
  /** Items the standard's own count rule requires at this paper's item count. */
  target: number;
}

export interface ComponentDifficulty {
  component: string;
  items: number;
  easy: number;
  medium: number;
  difficult: number;
  untagged: number;
  /** The frame's allocation for this component, or null when the frame does not name it. */
  frame: { easy: number; medium: number; difficult: number; items: number } | null;
}

export interface TosSummary {
  totalMarks: number;
  itemCount: number;
  /** Fraction of TAGGED marks, 0–1. Null when nothing is tagged. Never a guess. */
  bloom: { k: number; c: number; a: number } | null;
  /** Marks carried by blocks with no thinking level. 0 means the split above covers the paper. */
  bloomUntaggedMarks: number;
  /** Item COUNTS by band against the standard's count rule — the basis the standard itself uses
   *  for difficulty (items), not marks. Null only when there is nothing countable yet. */
  difficulty: DifficultyCount[] | null;
  /** Per-component breakdown, the dimension grand totals hide. */
  components: ComponentDifficulty[];
  violations: Violation[];
  passesGate: boolean;
}

const BAND_LABEL: Record<StandardBand, string> = { easy: "Easy", medium: "Medium", difficult: "Difficult" };
const BAND_OF: Record<"E" | "M" | "D", StandardBand> = { E: "easy", M: "medium", D: "difficult" };

/** The one component the standard binds absolutely: "Visual Reading always carries at least one
 *  difficult item" (EDEN_Department_Test_Standard_v2_2026-07-30.md §10, :657-658). Every other
 *  component's allocation is explicitly variable — see rail 3. */
const ALWAYS_ONE_DIFFICULT = "Visual Reading";

/**
 * The standard's difficulty rails, as severity-bearing checks.
 *
 * ⚠ This replaces `difficultyShares()`, which returned a share row and NOTHING ELSE — no rounding
 * rule, no per-component dimension, no severity — and which returned null (blanking the whole
 * panel) the moment ANY block was untagged. One untagged card silently hid the paper's entire
 * difficulty picture; 31 of 589 bank cards are in exactly that state (`F-2026-09-18-07`). The
 * counts below are reported over what IS tagged, and the untagged items are named out loud.
 */
function difficultyRails(
  blocks: EditorBlock[],
  grade: number | null,
  kind: PaperKind,
): { counts: DifficultyCount[] | null; components: ComponentDifficulty[]; violations: Violation[] } {
  const items = countableBlocks(blocks);
  const violations: Violation[] = [];
  if (items.length === 0) return { counts: null, components: [], violations };
  const componentOfBlock = componentsByBlock(blocks);

  const tally = { easy: 0, medium: 0, difficult: 0 };
  let untagged = 0;
  const byComponent = new Map<string, ComponentDifficulty>();
  for (const block of items) {
    const component = componentOfBlock.get(block.id) ?? UNALLOCATED;
    let row = byComponent.get(component);
    if (!row) {
      row = { component, items: 0, easy: 0, medium: 0, difficult: 0, untagged: 0, frame: null };
      byComponent.set(component, row);
    }
    row.items++;
    const band = block.difficulty ? BAND_OF[block.difficulty] : null;
    if (band) {
      tally[band]++;
      row[band]++;
    } else {
      untagged++;
      row.untagged++;
    }
  }

  const target = difficultyCountsFor(items.length);
  const counts: DifficultyCount[] = (["easy", "medium", "difficult"] as const).map((band) => ({
    band,
    label: BAND_LABEL[band],
    items: tally[band],
    target: target[band],
  }));

  // --- Rail 1: the paper's own totals against the standard's count rule. -------------------
  // Only meaningful once every item carries a tag — otherwise every band is short by definition
  // and three errors would fire that all say the same thing as rail 2. Starvation is reported by
  // rail 2, never mistaken for a pass here.
  if (untagged === 0) {
    for (const row of counts) {
      if (row.items !== row.target) {
        violations.push({
          code: `difficulty-count-${row.band}`,
          severity: "error",
          message:
            `${row.label}: ${row.items} item${row.items === 1 ? "" : "s"}; the standard requires ` +
            `${row.target} at ${items.length} items (${DIFFICULTY_RULE_TEXT}).`,
        });
      }
    }
  }

  // --- Rail 2: every component's E+M+D must equal its own item count. ----------------------
  // EDEN_TOS_AppSpec.md:205,211 — BLOCKING, because "grand totals alone let two compensating
  // typos through". An untagged item is exactly that failure: the component does not add up.
  for (const row of byComponent.values()) {
    if (row.untagged > 0) {
      violations.push({
        code: "difficulty-untagged",
        severity: "error",
        message:
          `${row.component}: ${row.easy + row.medium + row.difficult} of ${row.items} item` +
          `${row.items === 1 ? "" : "s"} carry a difficulty; ${row.untagged} do not. ` +
          `E + M + D must equal the component's own item count.`,
      });
    }
  }

  // --- Rail 5: a row carrying marks or items under NO component name. -------------------
  // EDEN_TOS_AppSpec.md:212, BLOCKING, and easy to leave off a list like this one -- which is
  // exactly what happened: this rail was missing until the independent review built a 30-item
  // hand-typed paper that satisfied every other rail and passed with zero violations.
  // A WORKSHEET is not a Table of Specifications and is not held to the frame (see rail 6).
  const unallocated = byComponent.get(UNALLOCATED);
  if (kind !== "worksheet" && unallocated) {
    violations.push({
      code: "component-missing",
      severity: "error",
      message:
        `${unallocated.items} item${unallocated.items === 1 ? "" : "s"} sit under no component. ` +
        `Put them under a section named for their component, or import them from a card. ` +
        `A row carrying marks under no component name cannot be placed on the Table of Specifications.`,
    });
  }

  // --- Rails 3, 4 and 6 need the grade's frame. ---------------------------------------------
  const group = grade === null ? null : groupForGrade(grade);
  if (!group) return { counts, components: [...byComponent.values()], violations };

  // --- Rail 6: the frame's components must all be PRESENT. ----------------------------------
  // EDEN_TOS_AppSpec.md:202 lists "the eight- (or six-) component frame" as BLOCKING. Without
  // this, rail 4 ("Visual Reading always carries a difficult item") is satisfied by DELETING
  // Visual Reading -- the independent review demonstrated exactly that: a 30-item paper with
  // Visual Reading's three items folded into Reading passed with one warning. A rail that a
  // paper can escape by removing the thing being checked is not a rail.
  if (kind !== "worksheet") {
    const present = new Set(byComponent.keys());
    const absent = group.components.filter((c) => c.items > 0 && !present.has(c.name));
    if (absent.length) {
      violations.push({
        code: "component-frame-incomplete",
        severity: "error",
        message:
          `The ${group.key} frame has ${group.components.length} components; this paper has no ` +
          `${absent.map((c) => c.name).join(", ")}. The frame is not a menu.`,
      });
    }
  }

  for (const row of byComponent.values()) {
    const frame = componentFrame(group, row.component);
    if (!frame) continue; // the frame does not name this component — rail 2 already reported it as unallocated
    row.frame = { easy: frame.easy, medium: frame.medium, difficult: frame.difficult, items: frame.items };
    // A WARNING, not an error, and deliberately: the standard permits per-component variation
    // ("a setter may vary the per-component allocation") while binding the totals rail 1 checks.
    if (row.easy !== frame.easy || row.medium !== frame.medium || row.difficult !== frame.difficult) {
      violations.push({
        code: `difficulty-component-${row.component.toLowerCase().replace(/\s+/g, "-")}`,
        severity: "warning",
        message:
          `${row.component}: ${row.easy}E/${row.medium}M/${row.difficult}D; the ${group.key} frame ` +
          `allocates ${frame.easy}E/${frame.medium}M/${frame.difficult}D. A setter may vary this, ` +
          `but the paper's totals may not.`,
      });
    }
  }

  // --- Rail 4: Visual Reading always carries at least one difficult item. ------------------
  // ⚠ This rail was first written to fire for ANY component the frame allocates a difficult item
  // to. That was wrong, and this file's own tests caught it: the frame allocates one to Grammar,
  // Reading and Writing as well, and rail 3 exists precisely because the standard PERMITS varying
  // those. A component-blind reading of the frame turned a permitted variation into an error.
  // The "always" belongs to Visual Reading alone (§10, :657-658), so the component is named from
  // the prose and the COUNT is corroborated against the frame — the test asserts every group
  // allocates it ≥1, so the two sources cannot drift apart unnoticed.
  for (const row of byComponent.values()) {
    if (row.component !== ALWAYS_ONE_DIFFICULT) continue;
    const frame = componentFrame(group, row.component);
    if (!frame || frame.difficult < 1 || row.items === 0) continue;
    if (row.difficult === 0) {
      violations.push({
        code: "difficulty-visual-reading",
        severity: "error",
        message:
          `${row.component} carries no difficult item. The ${group.key} frame allocates ` +
          `${frame.difficult}, and ${row.component} always carries at least one.`,
      });
    }
  }

  return { counts, components: [...byComponent.values()], violations };
}

/** The Bloom split over TAGGED blocks only, plus the marks that carry no level at all. */
function bloomOverTagged(blocks: EditorBlock[]): { split: TosSummary["bloom"]; untaggedMarks: number } {
  const items = countableBlocks(blocks);
  const untaggedMarks = items.filter((b) => !b.bloom).reduce((sum, b) => sum + (b.marks || 0), 0);
  const tagged = items.filter((b) => b.bloom);
  const inner = toTosAssessment(tagged);
  return { split: inner ? bloomSplit(inner) : null, untaggedMarks };
}

/** Neutral-worded summary for the TOS panel. Never blocks a SAVE; errors do block `tos_ok`. */
export function summarize(
  blocks: EditorBlock[],
  opts: { grade?: number | null; kind?: PaperKind } = {},
): TosSummary {
  const countable = countableBlocks(blocks);
  const totalMarks = countable.reduce((sum, b) => sum + (b.marks || 0), 0);
  const itemCount = countable.length;
  const { split, untaggedMarks } = bloomOverTagged(blocks);
  const rails = difficultyRails(blocks, opts.grade ?? null, opts.kind ?? "test");
  const notes: Violation[] = itemCountNotes(blocks).map((n) => ({
    code: n.code,
    severity: n.code === "item-count-collapsed" ? "error" : "warning",
    ref: n.blockId,
    message: n.message,
  }));

  const tos = toTosAssessment(blocks);
  if (!tos) {
    return {
      totalMarks, itemCount, bloom: split, bloomUntaggedMarks: untaggedMarks,
      difficulty: rails.counts, components: rails.components,
      violations: [...rails.violations, ...notes], passesGate: false,
    };
  }

  // tos.ts's own bloom-split findings are computed over the placeholder "k" that toTosAssessment
  // hands untagged items. They are arithmetic about a fiction, so they are dropped the moment
  // anything is untagged and replaced by one finding that names the real problem.
  const inner = validate(tos).filter((v) => !(untaggedMarks > 0 && v.code.startsWith("bloom-split-")));
  const violations = [...inner, ...rails.violations, ...notes];
  if (untaggedMarks > 0) {
    violations.push({
      code: "bloom-untagged",
      severity: "error",
      message:
        `${untaggedMarks} of ${totalMarks} mark${totalMarks === 1 ? "" : "s"} carry no thinking ` +
        `level, so the paper has no K/C/A split to check against 50/30/20. ` +
        `"Level of thinking K/C/A" is a required column of the Table of Specifications.`,
    });
  }

  return {
    totalMarks,
    itemCount,
    bloom: split,
    bloomUntaggedMarks: untaggedMarks,
    difficulty: rails.counts,
    components: rails.components,
    violations,
    // The gate is recomputed here rather than delegated to tos.ts's passesTosGate, because this
    // file's own rails carry errors tos.ts never sees. Same definition: no error, any warning.
    passesGate: violations.every((v) => v.severity !== "error"),
  };
}
